import { useState, useEffect } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { UkAreasPicker } from "../components/UkAreasPicker";
import { ServicesPicker } from "../components/ServicesPicker";
import { ApplicationDocumentUpload } from "../components/ApplicationDocumentUpload";
import {
  User, Phone, Buildings, Wrench, CurrencyGbp, Bank, FileText, CheckCircle, PaperPlaneTilt, CaretRight, CaretLeft
} from "@phosphor-icons/react";
import { toast } from "sonner";

const CONTACT_METHODS = ["Phone", "SMS", "WhatsApp", "Email", "NECOS App"];
const LEGAL_DOCS = [
  "Public Liability Insurance", "Employers Liability Insurance", "Professional Indemnity Insurance",
  "Personal Accident Insurance", "Modern Slavery & Human Trafficking Statement", "Anti-Bribery Statement",
  "Personal Data Handling Policy", "Terms & Conditions", "Client Privacy Policy",
  "Corporate Social Responsibility Policy"
];

const STEPS = [
  { id: "details", label: "Your Details", icon: User },
  { id: "contact", label: "Contact", icon: Phone },
  { id: "company", label: "Company", icon: Buildings },
  { id: "skills", label: "Skills", icon: Wrench },
  { id: "charges", label: "Charges", icon: CurrencyGbp },
  { id: "financial", label: "Financial", icon: Bank },
  { id: "legal", label: "Legal", icon: FileText },
  { id: "confirm", label: "Confirm", icon: CheckCircle },
];

const INITIAL = {
  first_name: "", last_name: "", position: "",
  email: "", contact_number: "", street_address: "", address_line_2: "",
  city: "", county: "", post_code: "", country: "United Kingdom",
  company_name: "", company_registration_number: "", vat_number: "",
  company_email: "", company_phone: "", company_website: "",
  uses_subcontractors: "", subcontractor_percentage: "", subcontractors_vetted: "",
  emergency_contact_methods: [],
  core_trade: [], specialist_skills: "", trade_qualifications: "",
  specialist_certifications: "", areas_covered: [],
  call_out_charges: "",
  payment_terms: "", bank_account_name: "", bank_sort_code: "", bank_account_number: "",
  legal_documents_held: [],
  documents: [],
  website_url: "",
  confirmation: false,
};

export default function SupplierApplicationPage() {
  const [step, setStep] = useState(0);
  const refreshToken = new URLSearchParams(window.location.search).get("refresh") || "";
  const [refreshCompany, setRefreshCompany] = useState("");
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [formToken, setFormToken] = useState("");
  const [schema, setSchema] = useState(null);

  // Bot protection: signed, time-limited token issued to the page; sent back with uploads + the final submission
  useEffect(() => {
    const fetchToken = () => API.get("/public/apply-token").then(({ data }) => setFormToken(data.token)).catch(() => {});
    fetchToken();
    const id = setInterval(fetchToken, 60 * 60 * 1000);
    API.get("/public/form-schema").then(({ data }) => setSchema(data)).catch(() => {});
    return () => clearInterval(id);
  }, []);
  const legalDocs = schema?.legal_documents || LEGAL_DOCS;
  const subPercentages = schema?.subcontractor_percentages || ["Under 25%", "Under 50%", "Over 50%", "Over 75%", "100%"];

  useEffect(() => {
    if (!refreshToken) return;
    API.get(`/supplier-applications/refresh-prefill/${refreshToken}`)
      .then(({ data }) => {
        setForm(prev => ({ ...prev, ...data.prefill }));
        setRefreshCompany(data.company_name || "your company");
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "This update link is invalid — submitting will create a new application"));
  }, [refreshToken]);

  const set = (key, value) => setForm(p => ({ ...p, [key]: value }));

  const toggleArray = (key, item) => {
    setForm(p => ({
      ...p,
      [key]: p[key].includes(item) ? p[key].filter(i => i !== item) : [...p[key], item]
    }));
  };

  const canAdvance = () => {
    switch (step) {
      case 0: return form.first_name && form.last_name;
      case 1: return form.email && form.contact_number;
      case 2: return form.company_email;
      case 7: return form.confirmation;
      default: return true;
    }
  };

  const handleSubmit = async () => {
    if (!form.confirmation) { toast.error("Please confirm the information is accurate"); return; }
    setSubmitting(true);
    try {
      const { data } = await API.post("/supplier-applications", { ...form, refresh_token: refreshToken, form_token: formToken, core_trade: (form.core_trade || []).join(", "), areas_covered: (form.areas_covered || []).join(", ") });
      setSubmitted(data);
      toast.success("Application submitted successfully!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center" data-testid="application-success">
          <div className="h-16 w-16 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-emerald-600 dark:text-emerald-400" weight="fill" />
          </div>
          <h1 className="text-2xl font-bold font-[Chivo] mb-2">Application Submitted</h1>
          <p className="text-sm text-muted-foreground mb-4">
            Your reference number is <strong className="text-foreground">{submitted.id}</strong>
          </p>
          <p className="text-xs text-muted-foreground">{submitted.message}</p>
          <Button className="mt-6" onClick={() => { setSubmitted(null); setForm(INITIAL); setStep(0); }} data-testid="submit-another-btn">
            Submit Another Application
          </Button>
        </div>
      </div>
    );
  }

  const current = STEPS[step];

  return (
    <div className="min-h-screen bg-background" data-testid="supplier-application-page">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <img
            src="https://customer-assets-jai6qajn.emergentagent.net/job_maintenance-hub-358/artifacts/mzvcle8b_NECOS_logo.webp"
            alt="necos.fm — Nationwide Emergency Call Out Services & Facilities Management"
            className="h-14 sm:h-16 w-auto"
            data-testid="apply-logo"
          />
          <h2 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight mt-3">{refreshCompany ? "Update Your Supplier Profile" : "Supplier Application"}</h2>
          <p className="text-xs text-muted-foreground mt-1">{refreshCompany ? "Review your pre-filled details and correct or complete anything that's changed" : "Complete the form below to apply to join our supplier network"}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {refreshCompany && (
          <div className="mb-6 border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4" data-testid="refresh-banner">
            <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">Updating profile for {refreshCompany}</p>
            <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-1">Your existing details are pre-filled below. Please check each section — especially services offered and areas covered — so we can match you to the right jobs. Your submission will be reviewed and merged into your existing record.</p>
          </div>
        )}
        {/* Step Indicator */}
        <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-2" data-testid="step-indicator">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === step;
            const isDone = i < step;
            return (
              <button key={s.id}
                onClick={() => i <= step && setStep(i)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all shrink-0 ${
                  isActive ? "bg-primary text-primary-foreground" :
                  isDone ? "bg-primary/10 text-primary cursor-pointer" :
                  "text-muted-foreground"
                }`}
                data-testid={`step-${s.id}`}
              >
                <Icon size={14} />
                <span className="hidden sm:inline">{s.label}</span>
                <span className="sm:hidden">{i + 1}</span>
              </button>
            );
          })}
        </div>

        {/* Form Content */}
        <div className="border border-border rounded-lg bg-card p-6" data-testid={`form-section-${current.id}`}>
          <h3 className="text-sm font-semibold font-[Chivo] mb-1 flex items-center gap-2">
            <current.icon size={16} className="text-primary" />
            {current.label}
          </h3>
          <div className="h-px bg-border my-4" />

          {/* Step 0: Your Details */}
          {step === 0 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="First Name *" value={form.first_name} onChange={v => set("first_name", v)} testId="input-first-name" />
                <Field label="Last Name *" value={form.last_name} onChange={v => set("last_name", v)} testId="input-last-name" />
              </div>
              <Field label="Position" value={form.position} onChange={v => set("position", v)} testId="input-position" />
            </div>
          )}

          {/* Step 1: Contact Details */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Email Address *" type="email" value={form.email} onChange={v => set("email", v)} testId="input-email" />
                <Field label="Contact Number *" type="tel" value={form.contact_number} onChange={v => set("contact_number", v)} testId="input-contact-number" />
              </div>
              <AddressAutocomplete
                publicMode
                label="Find address (UK)"
                value={form.street_address}
                onChange={v => set("street_address", v)}
                onSelect={(addr) => {
                  set("street_address", addr.street_address || addr.formatted_address || "");
                  set("city", addr.town_city || "");
                  set("county", addr.county || "");
                  set("post_code", addr.postcode || "");
                }}
                placeholder="Start typing your address or postcode..."
                data-testid="apply-address-autocomplete"
              />
              <Field label="Street Address" value={form.street_address} onChange={v => set("street_address", v)} testId="input-street" />
              <Field label="Address Line 2" value={form.address_line_2} onChange={v => set("address_line_2", v)} testId="input-address2" placeholder="Optional" />
              <div className="grid grid-cols-3 gap-4">
                <Field label="City" value={form.city} onChange={v => set("city", v)} testId="input-city" />
                <Field label="County" value={form.county} onChange={v => set("county", v)} testId="input-county" />
                <Field label="Post Code" value={form.post_code} onChange={v => set("post_code", v)} testId="input-postcode" />
              </div>
            </div>
          )}

          {/* Step 2: Company Details */}
          {step === 2 && (
            <div className="space-y-4">
              <Field label="Company Name" value={form.company_name} onChange={v => set("company_name", v)} testId="input-company-name" placeholder="Optional" />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Company Registration Number" value={form.company_registration_number} onChange={v => set("company_registration_number", v)} testId="input-company-reg" placeholder="Optional" />
                <Field label="VAT Number" value={form.vat_number} onChange={v => set("vat_number", v)} testId="input-vat" placeholder="Optional" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Company Email *" type="email" value={form.company_email} onChange={v => set("company_email", v)} testId="input-company-email" />
                <Field label="Company Phone" type="tel" value={form.company_phone} onChange={v => set("company_phone", v)} testId="input-company-phone" />
              </div>
              <Field label="Company Website" value={form.company_website} onChange={v => set("company_website", v)} testId="input-company-website" placeholder="https://" />
              <div className="border border-border rounded-md p-3 space-y-3" data-testid="subcontractor-section">
                <YesNoChoice
                  label="Do you use subcontractors?"
                  value={form.uses_subcontractors}
                  onChange={v => {
                    set("uses_subcontractors", v);
                    if (v === "No") { set("subcontractor_percentage", ""); set("subcontractors_vetted", ""); }
                  }}
                  testId="subcontractors-used"
                />
                {form.uses_subcontractors === "Yes" && (
                  <>
                    <div>
                      <Label className="text-xs mb-1 block">What percentage of the work do you outsource?</Label>
                      <Select value={form.subcontractor_percentage || undefined} onValueChange={v => set("subcontractor_percentage", v)}>
                        <SelectTrigger data-testid="subcontractor-percentage"><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          {subPercentages.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <YesNoChoice
                      label="Do you fully vet all subcontractors you use?"
                      value={form.subcontractors_vetted}
                      onChange={v => set("subcontractors_vetted", v)}
                      testId="subcontractors-vetted"
                    />
                  </>
                )}
              </div>
              <div>
                <Label className="text-xs mb-2 block">Emergency Call Out Contact Options</Label>
                <div className="flex flex-wrap gap-3">
                  {CONTACT_METHODS.map(m => (
                    <label key={m} className="flex items-center gap-2 cursor-pointer" data-testid={`contact-method-${m.toLowerCase().replace(/\s/g, "-")}`}>
                      <Checkbox checked={form.emergency_contact_methods.includes(m)} onCheckedChange={() => toggleArray("emergency_contact_methods", m)} />
                      <span className="text-xs">{m}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Skills & Qualifications */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs mb-1 block">Services Offered</Label>
                <ServicesPicker value={form.core_trade || []} onChange={v => set("core_trade", v)} testIdPrefix="apply-services" allowAdd={false} endpoint="/public/service-options" />
              </div>
              <Field label="Specialist Skills" value={form.specialist_skills} onChange={v => set("specialist_skills", v)} testId="input-specialist-skills" placeholder="Optional - additional specialist capabilities" />
              <Field label="Trade Qualifications" value={form.trade_qualifications} onChange={v => set("trade_qualifications", v)} testId="input-trade-quals" placeholder="e.g. NVQ Level 3, City & Guilds" />
              <Field label="Specialist Body Certifications" value={form.specialist_certifications} onChange={v => set("specialist_certifications", v)} testId="input-certifications" placeholder="e.g. Gas Safe, NICEIC, NAPIT" />
              <div>
                <Label className="text-xs mb-1 block">Areas Covered</Label>
                <UkAreasPicker value={form.areas_covered || []} onChange={v => set("areas_covered", v)} testIdPrefix="apply-areas" regions={schema?.areas?.general} countyGroups={schema?.areas ? Object.fromEntries(Object.entries(schema.areas).filter(([k]) => k !== "general")) : undefined} />
              </div>
            </div>
          )}

          {/* Step 4: Charges */}
          {step === 4 && (
            <div className="space-y-4">
              <FieldTextarea label="Call Out Charges" value={form.call_out_charges} onChange={v => set("call_out_charges", v)} testId="input-call-out-charges" placeholder="Please detail your standard call out charges, hourly rates, and any additional fees" />
            </div>
          )}

          {/* Step 5: Financial Information */}
          {step === 5 && (
            <div className="space-y-4">
              <Field label="Payment Terms" value={form.payment_terms} onChange={v => set("payment_terms", v)} testId="input-payment-terms" placeholder="e.g. 30 days net" />
              <Field label="Name on Bank Account" value={form.bank_account_name} onChange={v => set("bank_account_name", v)} testId="input-bank-name" />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Bank Sort Code" value={form.bank_sort_code} onChange={v => set("bank_sort_code", v)} testId="input-sort-code" placeholder="XX-XX-XX" />
                <Field label="Bank Account Number" value={form.bank_account_number} onChange={v => set("bank_account_number", v)} testId="input-account-number" />
              </div>
            </div>
          )}

          {/* Step 6: Legal Documentation */}
          {step === 6 && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs mb-2 block">Legal Documentation Held</Label>
                <p className="text-[10px] text-muted-foreground mb-3">Please tick all documents that your company holds</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {legalDocs.map(doc => (
                    <label key={doc} className="flex items-center gap-2 p-2.5 rounded-lg border border-border hover:bg-muted/30 transition-colors cursor-pointer" data-testid={`legal-doc-${doc.toLowerCase().replace(/\s+/g, "-")}`}>
                      <Checkbox checked={form.legal_documents_held.includes(doc)} onCheckedChange={() => toggleArray("legal_documents_held", doc)} />
                      <span className="text-xs">{doc}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs mb-2 block">Upload Documents <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <p className="text-[10px] text-muted-foreground mb-3">Attach copies of your certificates, insurances and policies — tag each with a type and expiry date so we can onboard you faster.</p>
                <ApplicationDocumentUpload value={form.documents} onChange={(docs) => set("documents", docs)} formToken={formToken} docTypes={schema?.document_type_values ? Object.values(schema.document_type_values) : undefined} />
              </div>
            </div>
          )}
          {step === 7 && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="space-y-4">
                <SummarySection title="Personal Details" items={[
                  ["Name", `${form.first_name} ${form.last_name}`],
                  ["Position", form.position],
                  ["Email", form.email],
                  ["Phone", form.contact_number],
                  ["Address", [form.street_address, form.city, form.county, form.post_code].filter(Boolean).join(", ")],
                ]} />
                <SummarySection title="Company Details" items={[
                  ["Company", form.company_name],
                  ["Reg No.", form.company_registration_number],
                  ["VAT", form.vat_number],
                  ["Company Email", form.company_email],
                  ["Website", form.company_website],
                  ["Uses Subcontractors", form.uses_subcontractors],
                  ...(form.uses_subcontractors === "Yes" ? [
                    ["% of Work Outsourced", form.subcontractor_percentage],
                    ["Subcontractors Fully Vetted", form.subcontractors_vetted],
                  ] : []),
                  ["Contact Methods", form.emergency_contact_methods.join(", ")],
                ]} />
                <SummarySection title="Skills & Qualifications" items={[
                  ["Services", (form.core_trade || []).join(", ")],
                  ["Specialist Skills", form.specialist_skills],
                  ["Qualifications", form.trade_qualifications],
                  ["Certifications", form.specialist_certifications],
                  ["Areas Covered", (form.areas_covered || []).join(", ")],
                ]} />
                <SummarySection title="Charges & Financial" items={[
                  ["Call Out Charges", form.call_out_charges],
                  ["Payment Terms", form.payment_terms],
                  ["Bank Account", form.bank_account_name],
                ]} />
                <SummarySection title="Legal Documents Held" items={
                  form.legal_documents_held.length > 0
                    ? form.legal_documents_held.map(d => [d, ""])
                    : [["None selected", ""]]
                } />
              </div>

              <div className="h-px bg-border" />
              <label className="flex items-start gap-3 p-4 rounded-lg border border-border cursor-pointer hover:bg-muted/30 transition-colors" data-testid="confirmation-checkbox">
                <Checkbox checked={form.confirmation} onCheckedChange={v => set("confirmation", v)} className="mt-0.5" />
                <span className="text-xs">I confirm that the submitted information is accurate and I consent to NECOS.FM processing this data for the purpose of supplier onboarding.</span>
              </label>
            </div>
          )}
        </div>

        {/* Navigation */}
        <input type="text" name="website_url" value={form.website_url} onChange={e => set("website_url", e.target.value)}
          autoComplete="off" tabIndex={-1} aria-hidden="true"
          style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }} />
        <div className="flex items-center justify-between mt-6">
          <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={step === 0} data-testid="prev-step-btn">
            <CaretLeft size={14} className="mr-1" /> Previous
          </Button>
          <span className="text-xs text-muted-foreground">{step + 1} of {STEPS.length}</span>
          {step < STEPS.length - 1 ? (
            <Button onClick={() => setStep(s => s + 1)} disabled={!canAdvance()} data-testid="next-step-btn">
              Next <CaretRight size={14} className="ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={!form.confirmation || submitting} data-testid="submit-application-btn">
              <PaperPlaneTilt size={14} className="mr-1" /> {submitting ? "Submitting..." : "Submit Application"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function YesNoChoice({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-xs mb-1.5 block">{label}</Label>
      <div className="flex gap-2">
        {["Yes", "No"].map(opt => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`px-5 py-1.5 text-xs rounded-md border transition-colors ${value === opt ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
            data-testid={`${testId}-${opt.toLowerCase()}`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, placeholder }) {
  return (
    <div>
      <Label className="text-xs" htmlFor={testId}>{label}</Label>
      <Input id={testId} type={type} value={value} onChange={e => onChange(e.target.value)} data-testid={testId} placeholder={placeholder} />
    </div>
  );
}

function FieldTextarea({ label, value, onChange, testId, placeholder }) {
  return (
    <div>
      <Label className="text-xs" htmlFor={testId}>{label}</Label>
      <Textarea id={testId} value={value} onChange={e => onChange(e.target.value)} className="text-xs h-20" data-testid={testId} placeholder={placeholder} />
    </div>
  );
}

function SummarySection({ title, items }) {
  const filtered = items.filter(([, v]) => v);
  if (filtered.length === 0) return null;
  return (
    <div className="p-3 rounded-lg bg-muted/30">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">{title}</p>
      <div className="grid gap-1">
        {filtered.map(([label, value], i) => (
          <div key={i} className="flex items-start gap-2 text-xs">
            {label && <span className="text-muted-foreground w-28 shrink-0">{label}:</span>}
            <span>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
